import React from "react";
import { useNavigate } from "react-router-dom";

const LearningManagementSystem = () => {
  const navigate = useNavigate();

  return (
    <section className="menu-section-bg">
      <div className="menu-container">

        {/* ===== HEADER ===== */}
        <div className="product-subtitleBg-header">
          <h1 className="product-titleBg">
            Learning Management System (LMS)
          </h1>
          <p className="product-subtitleBg">
            Deliver interactive digital learning, virtual classrooms, automated assessments,
            and rich multimedia course content anywhere, anytime.
          </p>
        </div>

        {/* ===== SECTION 1 ===== */}
        <div className="product-and-sol-mainCard ps-two-column-grid product-sol-card">
          <div className="enrollment-image-card product-sol-card-imageBox">
            <img
              src="../images/product&soluction/academicmanagement.jpg"
              alt="LMS Digital Learning"
              className="ps-main-image"
            />
          </div>

          <div>
            <h2 className="product-and-sol-sectionTitle">
              Modern Digital Learning Platform
            </h2>
            <p className="product-and-sol-section-text">
              Transform traditional learning with an interactive LMS designed for K-12 and higher
              education. Create dynamic courses, host live virtual classes, distribute assignments,
              and grade students effortlessly.
            </p>

            <ul className="product-and-sol-list">
              <li>✔ Interactive course creation & multimedia support</li>
              <li>✔ Online quizzes, assignments & auto-grading</li>
              <li>✔ Hybrid & virtual classroom integration</li>
              <li>✔ Adaptive learning paths & progress tracking</li>
            </ul>

            <div
              className="read-more-btn"
              onClick={() => navigate("/academic-management/lms-readmore")}
            >
              Read detailed overview →
            </div>
          </div>
        </div>

        {/* ===== SECTION 2 ===== */}
        <div className="product-and-sol-secondCard ps-two-column-grid product-sol-card">
          <div>
            <h2 className="product-and-sol-sectionTitle">
              Why a Modern LMS Belongs in Every Institution
            </h2>
            <p className="product-and-sol-section-text">
              Education is no longer restricted to physical classrooms. Our LMS bridges offline
              and online learning, keeping students engaged and enabling teachers to deliver
              personalized education at scale.
            </p>

            <ul className="product-and-sol-list">
              <li>✔ Fosters self-paced and collaborative learning</li>
              <li>✔ Centralizes all learning materials & resources</li>
              <li>✔ Provides instant feedback on tests & assignments</li>
              <li>✔ Integrates seamlessly with SIS and academic tools</li>
              <li>✔ Supports mobile learning on any device</li>
            </ul>

            <div
              className="read-more-btn"
              onClick={() => navigate("/contact#Contact-form")}
            >
              Contact Us →
            </div>
          </div>

          <div className="student-image-card product-sol-card-imageBox">
            <img
              src="../images/product&soluction/education-academy-certification-curriculum.jpg"
              alt="LMS Student Engagement"
              className="ps-main-image"
            />
          </div>
        </div>

        {/* ===== FEATURES ===== */}
        <div className="ps-feature-grid">

          <FeatureCard
            typeClass="credentialing"
            backgroundColor="#e9fdf3"
            icon={icons.course}
            title="Course & Content Authoring"
            description="Upload videos, PDFs, interactive quizzes, and structured learning modules."
          />

          <FeatureCard
            typeClass="wallet"
            backgroundColor="#f3ecff"
            icon={icons.virtualClass}
            title="Virtual Classrooms"
            description="Seamless video conference integration for live sessions, webinars, and recorded lectures."
          />

          <FeatureCard
            typeClass="human-resources"
            backgroundColor="#f1f6ff"
            icon={icons.assignment}
            title="Assignments & Grading"
            description="Online submission portals with rubric-based automated and manual grading tools."
          />

          <FeatureCard
            typeClass="education"
            backgroundColor="#fff3cc"
            icon={icons.discussion}
            title="Discussion Forums"
            description="Interactive student-teacher Q&A boards and collaborative group study spaces."
          />

          <FeatureCard
            typeClass="marketing"
            backgroundColor="#ffe6dc"
            icon={icons.analytics}
            title="Student Learning Analytics"
            description="Detailed insights on course completion rates, quiz scores, and study time tracking."
          />

          <FeatureCard
            typeClass="analytics"
            backgroundColor="#eaf9fa"
            icon={icons.gamification}
            title="Badges & Certificates"
            description="Earn achievements, points, and automated course completion certificates."
          />

        </div>

        {/* ===== LMS HIGHLIGHT wrapper ===== */}
        <div className="ps-highlight-wrapper">
          <div className="ps-highlight-card">

            <div className="ps-highlight-header">
              <h2 className="ps-highlight-title">
                Empower Teachers & Inspire Students
              </h2>
              <p className="ps-highlight-subtitle">
                Build a future-ready digital learning environment
              </p>
            </div>

            <div className="ps-highlight-body">
              <p className="ps-highlight-text">
                Unlock the full potential of digital education with our all-in-one Learning Management System.
              </p>

              <div className="ps-button-group">
                <button
                  className="Demo-button"
                  onClick={() => navigate("/contact#Contact-form")}
                >
                  Request a Demo
                </button>
              </div>
            </div>

          </div>
        </div>

      </div>
    </section>
  );
};

/* ===== SHARED FEATURE CARD ===== */
const FeatureCard = ({
  icon,
  title,
  description,
  backgroundColor,
  typeClass
}) => (
  <div
    className={`card product-soluction-card ${typeClass}`}
    style={{ backgroundColor }}
  >
    <div className="card-overlay"></div>

    <div className="circle">
      <div className="ps-card-icon">{icon}</div>
    </div>

    <h3 className="ps-card-title">{title}</h3>
    <p className="product-soluction-card-text">{description}</p>
  </div>
);

/* ===== ICONS ===== */
const icons = {
  course: (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" stroke="#5a5cff" strokeWidth="2" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" stroke="#5a5cff" strokeWidth="2" />
    </svg>
  ),
  virtualClass: (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
      <polygon points="23 7 16 12 23 17 23 7" stroke="#ff8c00" strokeWidth="2" strokeLinejoin="round" />
      <rect x="1" y="5" width="15" height="14" rx="2" stroke="#ff8c00" strokeWidth="2" />
    </svg>
  ),
  assignment: (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" stroke="#0ca85c" strokeWidth="2" />
      <rect x="8" y="2" width="8" height="4" rx="1" stroke="#0ca85c" strokeWidth="2" />
      <path d="M9 12l2 2 4-4" stroke="#0ca85c" strokeWidth="2" />
    </svg>
  ),
  discussion: (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" stroke="#e74c3c" strokeWidth="2" />
    </svg>
  ),
  analytics: (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" stroke="#6c5ce7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  gamification: (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="8" r="6" stroke="#0097e6" strokeWidth="2" />
      <path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11" stroke="#0097e6" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  ),
};

export default LearningManagementSystem;
